import { DailyNetworkStats } from '@daily-co/daily-js';
import { useDailyError, useDailyEvent } from '@daily-co/daily-react';
import {
  Alert,
  Badge,
  Button,
  Divider,
  DropdownMenu,
  Icon,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  tokens,
  Tooltip,
  useNotify,
} from '@hotjar/hotsauce';
import { useVideoService } from '@unmoderated/video-service';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalytics } from '../../utils/observability';
import { UserMediaError } from '../UserMediaError/UserMediaError';
import { ListItem } from '../ListItem/ListItem';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { useProjectParams } from '../../utils/hooks/useProjectParams';

export function SystemCheck() {
  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [networkThreshold, setNetworkThreshold] = useState<DailyNetworkStats['threshold']>();

  const analytics = getAnalytics();
  const interval = useRef<ReturnType<typeof setInterval>>();
  const { getParam } = useProjectParams();
  const isPreview = !!getParam('is_preview');
  const [isOffline, setIsOffline] = useState(isPreview);
  const { showToast } = useNotify();

  const { t } = useTranslation('user-test', { keyPrefix: 'system-check' });
  const {
    cameras,
    callObject,
    currentCam,
    currentMic,
    microphones,
    isSharingScreen,
    toggleCamera,
    updateCamera,
    toggleMicrophone,
    updateMicrophone,
    handleScreenShare,
  } = useVideoService();

  const { meetingError } = useDailyError();
  const meetingErrorMsg = meetingError?.errorMsg;

  const connectionStatus = {
    good: {
      textColor: tokens.color.text_default_inverse,
      backgroundColor: tokens.color.mint_700,
      text: t('connection.good.title'),
    },
    low: {
      textColor: tokens.color.text_default_inverse,
      backgroundColor: tokens.color.ember_700,
      text: t('connection.fair.title'),
    },
    'very-low': {
      textColor: tokens.color.text_default_inverse,
      backgroundColor: tokens.color.ember_700,
      text: t('connection.poor.title'),
    },
    offline: {
      textColor: tokens.color.text_default_inverse,
      backgroundColor: tokens.color.ui_overlay,
      text: 'Offline',
    },
  };

  const connectionStatusBadgeProps = isOffline
    ? connectionStatus.offline
    : networkThreshold
    ? connectionStatus[networkThreshold]
    : undefined;

  useEffect(() => {
    analytics.track('Page Viewed', {
      name: 'Engage Unmoderated Lobby - Set Up',
    });
  }, [analytics]);

  useEffect(() => {
    if (meetingErrorMsg) {
      showToast({ text: meetingErrorMsg, kind: 'danger' });
    }
  }, [meetingErrorMsg, showToast]);

  useDailyEvent(
    'camera-error',
    useCallback(() => {
      setGetUserMediaError(true);
    }, []),
  );

  useEffect(() => {
    if (!callObject) {
      return;
    }

    clearInterval(interval.current);

    interval.current = setInterval(async () => {
      const networkStats = await callObject?.getNetworkStats();
      setNetworkThreshold(networkStats.threshold);
    }, 1000);

    return () => {
      clearInterval(interval.current);
    };
  }, [callObject]);

  function onOffline() {
    setIsOffline(true);
  }

  function onOnline() {
    setIsOffline(false);
  }

  useEffect(() => {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  const onCameraToggle = () => {
    toggleCamera().catch((err: Error) => {
      const message = 'message' in err ? err.message : 'Error while requesting camera access.';
      showToast({ text: message, kind: 'danger' });
    });
  };
  const onMicrophoneToggle = () => {
    toggleMicrophone().catch((err: Error) => {
      const message = 'message' in err ? err.message : 'Error while requesting camera access.';
      showToast({ text: message, kind: 'danger' });
    });
  };

  const descriptions = t('screen-share.points.descriptions', {
    returnObjects: true,
  });

  return getUserMediaError ? (
    <UserMediaError />
  ) : (
    <ErrorBoundary componentName="SystemCheck">
      <Stack direction="column">
        <Divider />
        <ListItem
          title={`${t('screen-share.title')}*`}
          icon="browser"
          description={
            <Stack direction="column">
              <Text textType="subtitle2">{t('screen-share.points.title')}</Text>
              <Stack direction="column" spacing="x1">
                {Array.isArray(descriptions) &&
                  descriptions.map((text: string) => (
                    <Stack key={text}>
                      <Icon icon="selected" />
                      <Text>{text}</Text>
                    </Stack>
                  ))}
              </Stack>
            </Stack>
          }
          actions={
            isSharingScreen ? (
              <DropdownMenu
                trigger={
                  <Button
                    name={t('screen-share.action.sharing')}
                    kind="secondary"
                    size="s"
                    icon="caret_down_alt"
                    iconPosition="right"
                  >
                    {t('screen-share.action.sharing')}
                  </Button>
                }
              >
                <DropdownMenu.Item
                  name={t('screen-share.action.share-other')}
                  onClick={() => handleScreenShare({ stop: false })}
                  icon="share"
                >
                  {t('screen-share.action.share-other')}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  name={t('screen-share.action.stop-share')}
                  onClick={() => handleScreenShare({ stop: !!isSharingScreen })}
                  icon="close"
                >
                  {t('screen-share.action.stop-share')}
                </DropdownMenu.Item>
              </DropdownMenu>
            ) : (
              <Tooltip text={t('preview-disabled')} isDisabled={!isPreview}>
                <Button
                  name={t('screen-share.action.share')}
                  kind="primary"
                  size="s"
                  isDisabled={isPreview}
                  onClick={() => handleScreenShare({ stop: !!isSharingScreen })}
                >
                  {t('screen-share.action.share')}
                </Button>
              </Tooltip>
            )
          }
        />
        <Divider />
        <ListItem
          title={`${t('microphone.title')}*`}
          icon="audio"
          description={
            currentMic && (
              <Select
                size="s"
                name="micOptions"
                id="micSelect"
                className="system-check-list-select"
                onChange={(e) => updateMicrophone(e.target.value)}
                value={currentMic?.device?.deviceId}
              >
                {microphones.map((mic) => (
                  <option key={`mic-${mic.device.deviceId}`} value={mic.device.deviceId}>
                    {mic.device.label}
                  </option>
                ))}
              </Select>
            )
          }
          actions={
            <Tooltip text={t('preview-disabled')} isDisabled={!isPreview}>
              <div>
                <Switch
                  size="m"
                  ariaLabel="Microphone toggle"
                  isChecked={!!currentMic}
                  onChange={onMicrophoneToggle}
                  isDisabled={isPreview}
                />
              </div>
            </Tooltip>
          }
        />
        <Divider />
        <ListItem
          title="Camera"
          icon="recordings_old"
          description={
            currentCam && (
              <Select
                size="s"
                name="cameraOptions"
                id="cameraSelect"
                className="system-check-list-select"
                onChange={(e) => updateCamera(e.target.value)}
                value={currentCam?.device?.deviceId}
              >
                {cameras.map((camera) => (
                  <option key={`cam-${camera.device.deviceId}`} value={camera.device.deviceId}>
                    {camera.device.label}
                  </option>
                ))}
              </Select>
            )
          }
          actions={
            <Tooltip text={t('preview-disabled')} isDisabled={!isPreview}>
              <div>
                <Switch
                  size="m"
                  ariaLabel="Camera toggle"
                  isChecked={!!currentCam}
                  onChange={onCameraToggle}
                  isDisabled={isPreview}
                />
              </div>
            </Tooltip>
          }
        />
        <Divider />
        <ListItem
          title={t('connection.title')}
          icon="funnels"
          actions={
            <Loader size="m" isLoading={!connectionStatusBadgeProps}>
              <Badge
                size="s"
                kind="custom"
                textColor={connectionStatusBadgeProps?.textColor}
                color={connectionStatusBadgeProps?.backgroundColor}
              >
                {connectionStatusBadgeProps?.text}
              </Badge>
            </Loader>
          }
        />
        {networkThreshold === 'low' ? (
          <Alert title={t('connection.fair.alert.title')} kind="warning">
            {t('connection.fair.alert.title')}
          </Alert>
        ) : networkThreshold === 'very-low' ? (
          <Alert title={t('connection.poor.alert.title')} kind="danger">
            {t('connection.poor.alert.title')}
          </Alert>
        ) : null}
      </Stack>
    </ErrorBoundary>
  );
}
